import { Checkbox, Form, Input, Switch } from 'antd';

import { apiFetch } from '@/api/client';
import { type EmployeeCapability } from '@/api/generated/enums';
import { employeesKey, type Employee } from '@/api/masters';
import { submitVia, useApiMutation } from '@/api/mutations';
import { DatePicker } from '@/components/DatePicker';
import { FormDrawer } from '@/components/FormDrawer';
import { Notice } from '@/components/PageHeading';
import { toDayjs, toIsoDate } from '@core/format/dates';

import { capabilityHints, capabilityLabels, selectableCapabilities } from './capabilities';
import { keepOrReplace } from './secretFields';

export type Editing = { mode: 'create' } | { mode: 'edit'; employee: Employee };

export interface EmployeeFormValues {
  code: string;
  fullName: string;
  capabilities: EmployeeCapability[];
  mobile: string;
  /** Create only — scope is fixed once the row exists, as on every other master. */
  isShared?: boolean;
  designation?: string | null;
  department?: string | null;
  alternateMobile?: string | null;
  email?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  stateCode?: string | null;
  stateName?: string | null;
  pincode?: string | null;
  dateOfJoining?: unknown;
  dateOfLeaving?: unknown;

  /**
   * The three write-only documents. Empty means "untouched" here and becomes a null on the way out
   * — see {@link keepOrReplace}.
   */
  panNumber?: string | null;
  aadhaarNumber?: string | null;
  bankAccountNumber?: string | null;
  /** Ticked to remove what is stored. The only way to send the empty string that clears it. */
  clearPan?: boolean;
  clearAadhaar?: boolean;
  clearBankAccount?: boolean;
  bankAccountHolderName?: string | null;
  bankIfsc?: string | null;
  bankName?: string | null;
  notes?: string | null;
  isActive?: boolean;
}

/**
 * Create or correct a member of staff (spec Section 24).
 *
 * <p>
 * <strong>The code is create-only</strong>, like every other master's, and the reason is stronger
 * here than for a catalogue row: Section 24's payroll and Section 30.1's Tally export both key the
 * salary ledger on this string, so changing it silently re-points historical pay at a different
 * person. The mobile number is editable, exactly as the customer's and the vendor's are.
 * </p>
 *
 * <p>
 * <strong>Ticking nothing is allowed, and the twin drawer refuses it.</strong> `VendorDrawer` makes
 * capabilities required, because a vendor who does nothing for the shop is not a vendor. The
 * head-office accountant does none of the four things the rental loop hands out and is still on the
 * payroll, so there is no `required` rule here — only a sentence saying what leaving them all
 * unticked means, or the empty boxes read as an unfinished form.
 * </p>
 *
 * <p>
 * <strong>There is no docket-language control, deliberately.</strong> It is set from the print bar
 * on the sheet itself, exactly as a vendor's is, and that is what keeps "null means nobody has said
 * yet" a usable state: a select sitting in this form would be answered on the day the row was
 * created, by somebody who has never met the karigar, and the sheet would then never ask.
 * </p>
 */
export function EmployeeDrawer({ editing, onClose }: { editing: Editing; onClose: () => void }) {
  const [form] = Form.useForm<EmployeeFormValues>();
  const isEdit = editing.mode === 'edit';
  const existing = editing.mode === 'edit' ? editing.employee : undefined;

  // Watched so ticking "Remove" empties and disables the box above it: a form offering both at once
  // is one where somebody types a replacement, ticks remove, and cannot tell which will win.
  const clearPan = Form.useWatch('clearPan', form);
  const clearAadhaar = Form.useWatch('clearAadhaar', form);
  const clearBankAccount = Form.useWatch('clearBankAccount', form);

  const save = useApiMutation<Employee, EmployeeFormValues>({
    mutationFn: (values) => {
      const body = {
        ...values,
        dateOfJoining: toIsoDate(values.dateOfJoining) ?? null,
        dateOfLeaving: toIsoDate(values.dateOfLeaving) ?? null,
        panNumber: keepOrReplace(values.panNumber, values.clearPan),
        aadhaarNumber: keepOrReplace(values.aadhaarNumber, values.clearAadhaar),
        bankAccountNumber: keepOrReplace(values.bankAccountNumber, values.clearBankAccount),
      };

      return isEdit
        ? apiFetch<Employee>(`/api/employee/${existing?.id}`, { method: 'PUT', body })
        : apiFetch<Employee>('/api/employee', { method: 'POST', body });
    },
    success: isEdit ? 'Staff record updated.' : 'Staff record created.',
    invalidates: [employeesKey],
    onSuccess: onClose,
  });

  return (
    <FormDrawer<EmployeeFormValues>
      title={isEdit ? `Edit ${existing?.fullName ?? 'employee'}` : 'New employee'}
      open
      onClose={onClose}
      form={form}
      initialValues={
        existing
          ? {
              ...existing,
              dateOfJoining: toDayjs(existing.dateOfJoining),
              dateOfLeaving: toDayjs(existing.dateOfLeaving),
              // Never echoed back — there is no endpoint that would return one. An empty box over a
              // "•••• 4321 on file" hint is the honest rendering of a value nobody may read.
              panNumber: '',
              aadhaarNumber: '',
              bankAccountNumber: '',
              clearPan: false,
              clearAadhaar: false,
              clearBankAccount: false,
            }
          : {
              code: '',
              fullName: '',
              capabilities: [],
              mobile: '',
              isShared: false,
              isActive: true,
            }
      }
      onSubmit={(values) => void submitVia(save, values)}
      submitText={isEdit ? 'Save' : 'Create'}
      submitting={save.isPending}
      width={560}
    >
      {!isEdit && (
        <Form.Item
          name="code"
          label="Staff code"
          extra="Fixed once created — payroll and the Tally export key the salary ledger on it."
          rules={[{ required: true, message: 'A staff code is required.' }]}
        >
          <Input placeholder="EMP-004" />
        </Form.Item>
      )}

      <Form.Item
        name="fullName"
        label="Name"
        extra="The name on the payroll record. Somebody who also signs in keeps their own display name."
        rules={[{ required: true, message: 'A name is required.' }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        name="capabilities"
        label="What they do here"
        extra="Tick everything that applies. Leave them all unticked for somebody the rental loop never hands work to — the accountant, the owner."
      >
        <Checkbox.Group className="flex flex-col gap-2">
          {selectableCapabilities.map((capability) => (
            <Checkbox key={capability} value={capability}>
              <span className="font-medium">{capabilityLabels[capability]}</span>
              <span className="text-default ms-2 text-[12px]">{capabilityHints[capability]}</span>
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Form.Item>

      <Form.Item
        name="mobile"
        label="Mobile"
        extra="Unique across the whole company — if this number is already on file, that person is this one."
        rules={[{ required: true, message: 'A mobile number is required.' }]}
      >
        <Input placeholder="98241 01010" inputMode="tel" />
      </Form.Item>

      <div className="grid grid-cols-2 gap-x-3">
        <Form.Item name="designation" label="Designation" extra="What the shop calls them.">
          <Input placeholder="Master Tailor" />
        </Form.Item>

        <Form.Item name="department" label="Department">
          <Input placeholder="Workshop" />
        </Form.Item>
      </div>

      <Form.Item name="alternateMobile" label="Alternate number">
        <Input inputMode="tel" />
      </Form.Item>

      <Form.Item name="email" label="Email">
        <Input type="email" />
      </Form.Item>

      {!isEdit && (
        <Form.Item
          name="isShared"
          label="Shared across branches"
          valuePropName="checked"
          extra="For head office — the owner, the accountant. Most people are posted to one shop. Needs a company-wide role."
        >
          <Switch />
        </Form.Item>
      )}

      <div className="grid grid-cols-2 gap-x-3">
        <Form.Item name="dateOfJoining" label="Joined on">
          <DatePicker className="w-full" />
        </Form.Item>

        <Form.Item
          name="dateOfLeaving"
          label="Left on"
          extra="Recording this does not switch off their login — that is its own act, on the Users screen."
        >
          <DatePicker className="w-full" />
        </Form.Item>
      </div>

      <Form.Item name="addressLine1" label="Address">
        <Input placeholder="Line 1" />
      </Form.Item>

      <Form.Item name="addressLine2" label=" " colon={false}>
        <Input placeholder="Line 2" />
      </Form.Item>

      <div className="grid grid-cols-2 gap-x-3">
        <Form.Item name="city" label="City">
          <Input />
        </Form.Item>

        <Form.Item name="pincode" label="Pincode">
          <Input inputMode="numeric" />
        </Form.Item>

        <Form.Item name="stateName" label="State">
          <Input />
        </Form.Item>

        <Form.Item name="stateCode" label="State code" extra="24 for Gujarat.">
          <Input maxLength={2} inputMode="numeric" />
        </Form.Item>
      </div>

      <Notice tone="info" title="These three are stored and never shown again">
        A PAN, an Aadhaar and a salary account are encrypted the moment they are saved, and no
        screen — and no endpoint, for anybody — will read one back. What you see afterwards is the
        last four characters, which is what a person checks a payment against. Correcting one means
        typing it again, not looking at it first.
      </Notice>

      <SecretField
        name="panNumber"
        clearName="clearPan"
        label="PAN"
        placeholder="ABCDE1234F"
        onFile={existing?.panIsSet ? existing.panLast4 : null}
        clearing={Boolean(clearPan)}
      />

      <SecretField
        name="aadhaarNumber"
        clearName="clearAadhaar"
        label="Aadhaar"
        placeholder="1234 5678 9012"
        onFile={existing?.aadhaarIsSet ? existing.aadhaarLast4 : null}
        clearing={Boolean(clearAadhaar)}
      />

      <SecretField
        name="bankAccountNumber"
        clearName="clearBankAccount"
        label="Salary account"
        placeholder="Account number"
        onFile={existing?.bankAccountIsSet ? existing.bankAccountLast4 : null}
        clearing={Boolean(clearBankAccount)}
      />

      <Form.Item name="bankAccountHolderName" label="Account holder">
        <Input placeholder="As it reads at the bank" />
      </Form.Item>

      <div className="grid grid-cols-2 gap-x-3">
        <Form.Item name="bankIfsc" label="IFSC">
          <Input maxLength={11} />
        </Form.Item>

        <Form.Item name="bankName" label="Bank">
          <Input />
        </Form.Item>
      </div>

      <Form.Item name="notes" label="Notes">
        <Input.TextArea rows={2} />
      </Form.Item>

      {isEdit && (
        <Form.Item
          name="isActive"
          label="Still working here"
          valuePropName="checked"
          extra="Switching this off keeps every job and fitting that names them readable. It does not touch their login."
        >
          <Switch />
        </Form.Item>
      )}

      <Notice tone="info" title="A login is a separate thing">
        Most staff never sign in, and every login costs a seat on your plan. Giving somebody one, or
        attaching an account they already have, is its own act and is not built into this form yet.
      </Notice>
    </FormDrawer>
  );
}

/**
 * One write-only document: an empty box, what is on file beneath it, and a way to remove it.
 *
 * <p>
 * <strong>The empty box is why this is not three plain inputs.</strong> The server reads a null as
 * "leave what is stored alone" and an empty string as "clear it" — and an untouched text input
 * produces the empty string. Sent as typed, opening the drawer and pressing Save would wipe every
 * document on the row, which is the failure `UpdateEmployeeRequest`'s own remarks name. So typing
 * nothing becomes a null on the way out, and clearing has to be *said* — which is what the tick box
 * is.
 * </p>
 */
function SecretField({
  name,
  clearName,
  label,
  placeholder,
  onFile,
  clearing,
}: {
  name: 'panNumber' | 'aadhaarNumber' | 'bankAccountNumber';
  clearName: 'clearPan' | 'clearAadhaar' | 'clearBankAccount';
  label: string;
  placeholder: string;
  /** The last four characters of what is stored, or null when nothing is. */
  onFile: string | null;
  clearing: boolean;
}) {
  return (
    <>
      <Form.Item
        name={name}
        label={label}
        extra={
          onFile
            ? clearing
              ? 'Will be removed when you save.'
              : `•••• ${onFile} on file — type a new one to replace it, or leave this empty to keep it.`
            : undefined
        }
      >
        <Input placeholder={clearing ? '' : placeholder} disabled={clearing} />
      </Form.Item>

      {onFile && (
        <Form.Item name={clearName} valuePropName="checked" className="-mt-2">
          <Checkbox>
            <span className="text-[13px]">Remove the {label.toLowerCase()} on file</span>
          </Checkbox>
        </Form.Item>
      )}
    </>
  );
}
